import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Key, Heart, List, X } from "@phosphor-icons/react";
import { BRAND } from "@/lib/constants";
import useFavorites from "@/hooks/useFavorites";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home" },
  { to: "/listings", label: "Listings", testid: "nav-listings" },
  { to: "/mls-search", label: "MLS Search", testid: "nav-mls" },
  { to: "/financing", label: "Financing", testid: "nav-financing" },
  { to: "/home-value", label: "Home Value", testid: "nav-home-value" },
  { to: "/buy", label: "Buy", testid: "nav-buy" },
  { to: "/sell", label: "Sell", testid: "nav-sell" },
  { to: "/listings?listing_type=rent", label: "Rent", testid: "nav-rent" },
];

export default function SiteNav({ showAbout = false, transparent = false }) {
  const { count } = useFavorites();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <nav
      className={`fixed top-0 inset-x-0 z-50 backdrop-blur-md border-b border-white/5 ${
        transparent ? "bg-background/40" : "bg-background/70"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between gap-3">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 text-white tracking-[0.2em] text-sm shrink-0" onClick={close}>
          <Key size={20} weight="duotone" className="text-brand" /> {BRAND.mark}
        </Link>

        <div className="hidden lg:flex items-center gap-6 xl:gap-7 text-sm text-white/60">
          {LINKS.map((l) => (
            <Link key={l.testid} to={l.to} className="hover:text-white transition-colors" data-testid={l.testid}>
              {l.label}
            </Link>
          ))}
          {showAbout && (
            <a href="#about" className="hover:text-white transition-colors" data-testid="nav-about">About</a>
          )}
        </div>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/favorites"
            data-testid="nav-favorites"
            className="relative flex items-center gap-1.5 text-white/70 hover:text-brand transition-colors p-2"
            aria-label="Saved homes"
          >
            <Heart size={20} weight={count ? "fill" : "regular"} className={count ? "text-brand" : ""} />
            {count > 0 && (
              <span data-testid="nav-favorites-count" className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-brand px-1 text-[10px] font-semibold text-black">{count}</span>
            )}
          </Link>
          <Link
            to="/book"
            data-testid="nav-book"
            className="hidden sm:inline-block text-xs uppercase tracking-[0.15em] bg-brand text-black px-4 py-2.5 rounded-sm hover:bg-brand-hover transition-all"
          >
            Book a call
          </Link>
          <Link
            to="/login"
            data-testid="nav-login"
            className="hidden md:inline-block text-xs uppercase tracking-[0.15em] text-white/70 border border-white/15 px-4 py-2.5 rounded-sm hover:border-brand hover:text-brand transition-all"
          >
            Agent Login
          </Link>
          <button
            type="button"
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden flex h-10 w-10 items-center justify-center rounded-sm border border-white/15 text-white hover:border-brand hover:text-brand transition-colors"
          >
            {open ? <X size={20} weight="bold" /> : <List size={20} weight="bold" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0B0C10]/98" data-testid="nav-mobile-panel">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
            <div className="flex flex-col gap-1">
              {LINKS.map((l) => (
                <Link
                  key={`m-${l.testid}`}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  onClick={close}
                  className="px-3 py-3 rounded-sm text-sm text-white/80 hover:bg-white/5 hover:text-brand transition-colors"
                >
                  {l.label}
                </Link>
              ))}
              {showAbout && (
                <a
                  href="#about"
                  data-testid="mobile-nav-about"
                  onClick={close}
                  className="px-3 py-3 rounded-sm text-sm text-white/80 hover:bg-white/5 hover:text-brand transition-colors"
                >
                  About
                </a>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-2">
              <Link
                to="/book"
                data-testid="mobile-nav-book"
                onClick={close}
                className="text-center text-xs uppercase tracking-[0.15em] bg-brand text-black px-5 py-3 rounded-sm font-medium"
              >
                Book a call
              </Link>
              <Link
                to="/financing"
                data-testid="mobile-nav-financing-cta"
                onClick={close}
                className="text-center text-xs uppercase tracking-[0.15em] border border-brand/50 text-brand px-5 py-3 rounded-sm"
              >
                Need a mortgage?
              </Link>
              <Link
                to="/listings?listing_type=rent"
                data-testid="mobile-nav-rent-cta"
                onClick={close}
                className="text-center text-xs uppercase tracking-[0.15em] border border-white/20 text-white px-5 py-3 rounded-sm"
              >
                I&apos;m Renting
              </Link>
              <Link
                to="/login"
                data-testid="mobile-nav-login"
                onClick={close}
                className="text-center text-xs uppercase tracking-[0.15em] text-white/70 border border-white/15 px-5 py-3 rounded-sm"
              >
                Agent Login
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
